import {
  Briefcase,
  GraduationCap,
  Code2,
  RefreshCw,
  Compass,
  Users,
  BookOpen,
  Rocket,
  Sparkles,
  Target,
  Wrench,
  Cog,
  HeartHandshake,
  Zap,
  Waves,
} from "lucide-react";

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1656236607879-cd489955e17b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxjb25maWRlbnQlMjBJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMG1lbnRvciUyMHdhcm0lMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTA1ODIxMjB8MA&ixlib=rb-4.1.0&q=85",
  about: "https://images.unsplash.com/photo-1573497019236-17f8177b81e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBpbmRpYW4lMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDU4ODkwMnww&ixlib=rb-4.1.0&q=85",
  workshop: "https://images.unsplash.com/photo-1552181903-a6af3a3d159d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHByb2Zlc3Npb25hbHMlMjB3b3Jrc2hvcCUyMG1lbnRvcmmlbmclMjBzZXNzaW9uJTIwd2FybXxlbnwwfHx8fDE3OTA1ODIxMjl8MA&ixlib=rb-4.1.0&q=85",
};

export const STATS = [
  { value: 1000, suffix: "+", label: "Graduates Mentored" },
  { value: 4000, suffix: "+", label: "Underprivileged Students Reached" },
  { value: 100, suffix: "+", label: "1:1 Mentoring Journeys" },
  { value: 100000, suffix: "", label: "Lives — The Mission Ahead" },
];

export const AUDIENCES = [
  { icon: Briefcase, title: "Corporate Employees", text: "Feeling stuck, reactive, or burnt out in their current role.", span: "lg:col-span-3" },
  { icon: GraduationCap, title: "Freshers & First-Job Holders", text: "Navigating the shock of real-world work life.", span: "lg:col-span-3" },
  { icon: Code2, title: "IT Professionals & Tech Employees", text: "Managing high-pressure, deadline-driven environments.", span: "lg:col-span-2" },
  { icon: RefreshCw, title: "Young Professionals", text: "Stuck in a loop — same patterns, same frustrations, no real movement.", span: "lg:col-span-2" },
  { icon: Compass, title: "Career Switchers", text: "Questioning whether they're on the right path at all.", span: "lg:col-span-2" },
  { icon: Users, title: "Working Professionals", text: "Dealing with difficult managers, teams, or workplace dynamics.", span: "lg:col-span-3" },
  { icon: BookOpen, title: "Students & Young Adults", text: "Preparing to enter the workforce with clarity.", span: "lg:col-span-3" },
  { icon: Rocket, title: "Entrepreneurs & Business Owners", text: "Running on reactive mode instead of a real system.", span: "lg:col-span-3" },
  { icon: Sparkles, title: "Anyone Seeking Authentic Growth", text: "Anyone performing a version of success that isn't really theirs.", span: "lg:col-span-3" },
];

export const SERVICES = [
  {
    num: "01",
    title: "Live Webinars",
    text: "Interactive sessions like The Authentic Career & Life Reset — designed to help you spot the patterns keeping you stuck, redefine success on your own terms, and walk away with a real roadmap forward.",
    cta: "Explore Webinars",
  },
  {
    num: "02",
    title: "The 3-Day Mentoring Workshop",
    text: "An intensive, live, interactive program — 3 hours daily over 3 days, in a small cohort — covering self-work and old patterns, new habits and performance, and career, wealth, and relationship planning. A complete reset across every part of your life.",
    cta: "Explore the Workshop",
  },
  {
    num: "03",
    title: "Digital Products & Self-Paced Guides",
    text: "Practical, research-backed resources you can implement on your own time — tools that keep working long after the session ends.",
    cta: "Explore Resources",
  },
  {
    num: "04",
    title: "1:1 Mentoring",
    text: "Personalized mentoring for individuals ready to go deeper than a group setting allows — built around your specific goals, blocks, and circumstances.",
    cta: "Book a Strategy Call — ₹199",
    highlight: true,
  },
];

export const BENEFITS = [
  { icon: Target, title: "Clarity, Not Confusion", text: "Gain clarity about your goals, direction, and the patterns that may be holding you back." },
  { icon: Wrench, title: "Real Tools, Not Motivation", text: "Work with practical tools and research-backed techniques rather than relying on temporary inspiration." },
  { icon: Cog, title: "A System You Can Actually Run", text: "Build practical systems you can apply in your daily life." },
  { icon: HeartHandshake, title: "Support With Full Dedication", text: "Receive dedicated support throughout your growth journey." },
  { icon: Zap, title: "Faster Results Because You Start Now", text: "Take action now instead of postponing your growth." },
  { icon: Waves, title: "A Ripple Effect Beyond Yourself", text: "Your growth can contribute to stronger relationships, families, and communities." },
];

export const DIFFERENTIATORS = [
  { title: "A Deep Understanding of Young Minds", text: "Years of hands-on mentoring across very different backgrounds have given me a real, practiced understanding of how young people think, get stuck, and grow — not theoretical, but built from direct experience." },
  { title: "A Scientist's Background, Not Just a Coach's Instinct", text: "Starting in Biochemistry research before moving into psychology means my approach is built on evidence and process, not just intuition or personality." },
  { title: "Real Techniques, Not Recycled Motivation", text: "Everything I teach is grounded in real processes happening in the body, mind, and soul — backed by clinical research — not repackaged inspirational content." },
  { title: "Experience Across Every Background", text: "Working with underprivileged students through government programs, alongside corporate professionals and freshers, means I understand what actually blocks potential — not just what looks like a block on paper." },
  { title: "Complete Dedication, Not a Transaction", text: "I don't sell a dream and walk away. I stay committed to seeing the work through with full dedication — no shortcuts, no half-measures." },
  { title: "A Bias Toward Action, Not Just Insight", text: "The work doesn't stop at \u201caha\u201d moments — it's built to move you into action now, because the cost of waiting is real and often invisible until it's too late." },
];

export const VALUES = [
  "Authenticity Over Performance",
  "Evidence Over Motivation",
  "Access Over Exclusivity",
  "Systems Over Willpower",
  "Depth Over Speed",
  "Dedication Over Promises",
  "Action Now Over \u201cSomeday\u201d",
];

export const FAQS = [
  { q: "Who is this best for?", a: "Working professionals who feel stuck and want to move. If you are unsure about your career path, if you doubt yourself more than you should, if you keep starting things and dropping them, or if you have no plan for the next five years, this is built for you." },
  { q: "Will this help if I work in a small company or have a regular job?", a: "Yes. None of this needs a big brand name or a rich network. The approach is relevant whether you are in your first job or your tenth year." },
  { q: "I've tried self-help books and videos before. How is this different?", a: "Books tell you what to think. This tells you what to do. The mentoring approach focuses on practical action steps, exercises, and systems you can apply in your everyday life." },
  { q: "Do you guarantee results?", a: "I do not make any promises about your results. What you get out of mentoring depends on what you put in." },
  { q: "What can I expect from the Strategy Call?", a: "The Strategy Call is an opportunity to discuss your goals, challenges, and current situation and explore how mentoring can support your next steps." },
  { q: "Why is the Strategy Call only ₹199?", a: "I believe taking the first step toward personal and career growth should be accessible. The Strategy Call is priced at ₹199 to give you an opportunity to discuss your goals, challenges, and current situation and explore how mentoring can support your next steps. It's a chance to start with clarity and take a meaningful step toward the future you want to build." },
  { q: "How much time do I need to give to mentoring?", a: "The time commitment depends on the mentoring service or program you choose. The 3-Day Mentoring Workshop runs for 3 hours daily over 3 days." },
  { q: "What if I need help after booking?", a: "For questions or assistance, you can contact Fouzia Tarannum using the contact details provided on the website." },
  { q: "How can I get started?", a: "You can book a Strategy Call for ₹199 to discuss your current situation and explore the next steps toward your goals." },
];

export const MARQUEE_WORDS = ["Clarity", "Systems", "Evidence", "Action", "Growth", "Purpose", "Courage", "Momentum"];

export const CONTACT = {
  phone: "+91 89779 63555",
  email: "ftarannum08@gmail.com",
  website: "www.fouziatarannum.com",
};
